import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { 
  Search as SearchIcon, Users, User, UserPlus, UserMinus, 
  Globe, Lock, Filter, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filter, setFilter] = useState(searchParams.get('type') || 'all');
  const [results, setResults] = useState({ users: [], communities: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const performSearch = useCallback(async (searchQuery, searchType) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ users: [], communities: [] });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: searchQuery, type: searchType },
        withCredentials: true
      });
      // Backend returns 'experts' but frontend expects 'users'
      const data = response.data;
      setResults({
        users: data.experts || data.users || [],
        communities: data.communities || []
      });
      setSearched(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = searchParams.get('q');
    const type = searchParams.get('type') || 'all';
    if (q) {
      setQuery(q);
      setFilter(type);
      performSearch(q, type);
    }
  }, [searchParams, performSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.length >= 2) {
      setSearchParams({ q: query, type: filter });
      performSearch(query, filter);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (query.length >= 2) {
      setSearchParams({ q: query, type: newFilter });
      performSearch(query, newFilter);
    }
  };

  const handleFollow = async (userId, isFollowing, e) => {
    e.stopPropagation();
    try {
      if (isFollowing) {
        await axios.delete(`${API}/users/${userId}/follow`, { withCredentials: true });
      } else {
        await axios.post(`${API}/users/${userId}/follow`, {}, { withCredentials: true });
      }
      // Update local state
      setResults(prev => ({
        ...prev,
        users: prev.users.map(u => 
          u.user_id === userId ? { ...u, is_following: !isFollowing } : u
        )
      }));
    } catch (error) {
      console.error('Error following user:', error);
    }
  };

  const handleJoinCommunity = async (communityId, isMember, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/communities/${communityId}/join`, {}, { withCredentials: true });
      setResults(prev => ({
        ...prev,
        communities: prev.communities.map(c =>
          c.community_id === communityId ? { ...c, is_member: !isMember } : c
        )
      }));
    } catch (error) {
      console.error('Error joining community:', error);
    }
  };

  const totalResults = results.users.length + results.communities.length;

  return (
    <div className="max-w-4xl mx-auto" data-testid="search-page">
      {/* Search Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Rechercher</h1>
        
        <form onSubmit={handleSearch}>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher des utilisateurs ou des communautés..."
                className="pl-10 h-12"
              />
            </div>
            <Button type="submit" className="bg-gradient-to-r from-purple-500 to-pink-500 h-12 px-6">
              Rechercher
            </Button>
          </div>
        </form>

        {/* Filters */}
        <div className="flex items-center gap-2 mt-4">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-500 mr-2">Filtrer :</span>
          {[
            { value: 'all', label: 'Tout', icon: null },
            { value: 'users', label: 'Utilisateurs', icon: User },
            { value: 'communities', label: 'Communautés', icon: Users }
          ].map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => handleFilterChange(value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                filter === value
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {Icon && <Icon className="h-4 w-4" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : searched ? (
        <>
          {/* Results Count */}
          <p className="text-gray-500 mb-4">
            {totalResults} résultat{totalResults !== 1 ? 's' : ''} pour "{query}"
          </p>

          {totalResults === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <SearchIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun résultat</h3>
              <p className="text-gray-500">
                Essayez avec d'autres mots-clés ou modifiez vos filtres
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Users Results */}
              {(filter === 'all' || filter === 'users') && results.users.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                    <User className="h-5 w-5 text-purple-500" />
                    <h2 className="font-semibold text-gray-900">Utilisateurs ({results.users.length})</h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {results.users.map((user) => (
                      <div
                        key={user.user_id}
                        onClick={() => navigate(`/app/profile/${user.user_id}`)}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-purple-500 text-white font-semibold">
                              {user.display_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900">{user.display_name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                            {user.bio && (
                              <p className="text-sm text-gray-400 line-clamp-1 max-w-md">{user.bio}</p>
                            )}
                          </div>
                        </div>
                        
                        {currentUser?.user_id !== user.user_id && (
                          <Button
                            size="sm"
                            variant={user.is_following ? "outline" : "default"}
                            onClick={(e) => handleFollow(user.user_id, user.is_following, e)}
                            className={!user.is_following ? "bg-gradient-to-r from-cyan-500 to-purple-500" : ""}
                          >
                            {user.is_following ? (
                              <>
                                <UserMinus className="h-4 w-4 mr-1" />
                                Suivi
                              </>
                            ) : (
                              <>
                                <UserPlus className="h-4 w-4 mr-1" />
                                Suivre
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Communities Results */}
              {(filter === 'all' || filter === 'communities') && results.communities.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                    <Users className="h-5 w-5 text-pink-500" />
                    <h2 className="font-semibold text-gray-900">Communautés ({results.communities.length})</h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {results.communities.map((community) => (
                      <div
                        key={community.community_id}
                        onClick={() => navigate(`/app/community/${community.community_id}`)}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={community.avatar_url} />
                            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold">
                              {community.name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{community.name}</p>
                              {community.is_public ? (
                                <Globe className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Lock className="h-3.5 w-3.5 text-gray-400" />
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              {community.member_count} membres • {community.category}
                            </p>
                            {community.description && (
                              <p className="text-sm text-gray-400 line-clamp-1 max-w-md">{community.description}</p>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant={community.is_member ? "outline" : "default"}
                          onClick={(e) => handleJoinCommunity(community.community_id, community.is_member, e)}
                          className={!community.is_member ? "bg-gradient-to-r from-purple-500 to-pink-500" : ""}
                        >
                          {community.is_member ? "Membre" : "Rejoindre"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <SearchIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Recherchez des utilisateurs ou des communautés</h3>
          <p className="text-gray-500">
            Entrez au moins 2 caractères pour lancer la recherche
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
